import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { DEFAULT_LOCATION, QUICK_LOCATIONS } from "@/lib/quickLocations";
import { searchCity } from "@/lib/geocoding";
import api from "@/lib/api";
import { toast } from "sonner";

const LocationCtx = createContext(null);

const CACHE_KEY = "thaigayu_location";
const AUTODETECT_KEY = "thaigayu_location_autodetected";

/**
 * Best-effort: turn an IANA timezone like "Europe/London" or
 * "America/Los_Angeles" or "Asia/Kolkata" into a city name suitable for
 * geocoding. We strip the region prefix and turn underscores into spaces.
 *
 * Tricky cases: "America/Argentina/Buenos_Aires" -> "Buenos Aires" (use last
 * segment), "Etc/GMT" -> falsy (no usable city).
 */
function cityFromTimezone(tz) {
  if (!tz || typeof tz !== "string" || tz.startsWith("Etc/")) return null;
  const parts = tz.split("/");
  if (parts.length < 2) return null;
  const last = parts[parts.length - 1].replace(/_/g, " ").trim();
  return last || null;
}

/**
 * Auto-detect the user's home city from their browser timezone. Quick-pick
 * locations are checked first (zero network), then Open-Meteo geocoding is
 * used as a fallback. Returns null if nothing usable is found.
 */
async function detectLocationFromTimezone() {
  let tz;
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch { return null; }
  if (!tz) return null;

  // Fast path: timezone exactly matches a quick-pick city.
  const quick = QUICK_LOCATIONS.find((q) => q.timezone === tz);
  if (quick) return quick;

  // Smart path: derive the city name and look it up via Open-Meteo. Prefer
  // the result whose timezone matches the browser's exactly, otherwise take
  // the first result (geocoding sorts by relevance).
  const city = cityFromTimezone(tz);
  if (!city) return null;
  const results = await searchCity(city);
  if (!results.length) return null;
  const exact = results.find((r) => r.timezone === tz);
  const pick = exact || results[0];
  return {
    name: pick.name,
    lat: pick.lat,
    lng: pick.lng,
    country: pick.country || "",
    timezone: pick.timezone || tz,
    displayName: pick.country ? `${pick.name}, ${pick.country}` : pick.name,
  };
}

export function LocationProvider({ children }) {
  const { user, refresh } = useAuth();
  const autoSavedRef = useRef(false);
  const [location, setLocationState] = useState(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) return JSON.parse(cached);
    } catch { /* ignore */ }
    return DEFAULT_LOCATION;
  });

  // If the user is signed in and has a primary_location, that always wins.
  useEffect(() => {
    if (user && user.primary_location) {
      const pl = user.primary_location;
      setLocation({
        name: pl.name,
        lat: pl.lat,
        lng: pl.lng,
        country: pl.country || "",
        timezone: pl.timezone,
        displayName: pl.country ? `${pl.name}, ${pl.country}` : pl.name,
      });
    }
  }, [user]);

  // Brand-new accounts: when a user signs in/up and has zero saved locations
  // AND no primary location, persist the currently-active (auto-detected or
  // chosen) city to their account so the Dashboard isn't empty.
  // Runs at most once per session via autoSavedRef.
  useEffect(() => {
    if (!user) return;
    if (autoSavedRef.current) return;
    const hasSaved = (user.saved_locations || []).length > 0;
    if (hasSaved || user.primary_location) {
      autoSavedRef.current = true; // user already set up, no need
      return;
    }
    if (!location || location.lat == null || location.lng == null) return;
    autoSavedRef.current = true;
    (async () => {
      try {
        await api.post("/me/locations", {
          name: location.name, lat: location.lat, lng: location.lng,
          country: location.country || "", timezone: location.timezone,
        });
        await refresh();
        toast.success(`Set ${location.displayName || location.name} as your location`);
      } catch {
        // Silent — the empty Dashboard search-add will still let them add manually.
      }
    })();
  }, [user, location, refresh]);

  // First-visit auto-detect: runs once per device. We deliberately do NOT
  // bail out just because there's a cached location, because pre-v5.4 builds
  // wrote arbitrary caches (e.g. Singapore after a QA chip-click) that should
  // be overwritten by the user's real timezone. The `AUTODETECT_KEY` flag is
  // the source of truth for "we've already run the detect on this device".
  //
  // To preserve any manual selection that already matches the user's timezone
  // (e.g. someone in Mumbai who explicitly picked Mumbai pre-v5.4), we only
  // overwrite when the cached location's timezone differs from the detected
  // timezone.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (localStorage.getItem(AUTODETECT_KEY) === "1") return;
        if (user) return; // signed-in users' primary location wins
      } catch { /* ignore */ }

      const detected = await detectLocationFromTimezone();
      const markDone = () => {
        try { localStorage.setItem(AUTODETECT_KEY, "1"); } catch { /* ignore */ }
      };
      if (cancelled) return;
      if (!detected) { markDone(); return; }

      // If the existing cache already matches the browser's timezone, the
      // user is on their home turf — keep what's cached.
      let cached;
      try {
        const raw = localStorage.getItem(CACHE_KEY);
        cached = raw ? JSON.parse(raw) : null;
      } catch { cached = null; }
      if (cached && cached.timezone === detected.timezone) {
        markDone();
        return;
      }

      setLocation(detected);
      markDone();
      // One-shot nudge so users understand the city was picked for them and
      // that the location pill is interactive.
      toast.message(`Showing your sky from ${detected.displayName}.`, {
        description: "Tap the location to change.",
        duration: 6000,
      });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLocation = (loc) => {
    setLocationState(loc);
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(loc)); } catch { /* ignore */ }
  };

  return (
    <LocationCtx.Provider value={{ location, setLocation }}>
      {children}
    </LocationCtx.Provider>
  );
}

export const useLocation = () => useContext(LocationCtx);
